/**
 * Responsive utility functions for mobile and landscape detection
 */

/**
 * Determines if the current device/viewport should be considered mobile
 * @param width - Current viewport width
 * @returns true if device should be treated as mobile
 */
export const isMobileDevice = (width: number): boolean => {
  return width <= 768 || (width <= 1024 && "ontouchstart" in window);
};

/**
 * Determines if the screen is small (mobile portrait)
 * @param width - Current viewport width
 * @returns true if screen should be considered small
 */
export const isSmallScreen = (width: number): boolean => {
  return width < 768;
};

/**
 * Determines if the device is in mobile horizontal/landscape mode
 * @param width - Current viewport width
 * @param height - Current viewport height
 * @returns true if device is in mobile landscape mode
 */
export const isMobileHorizontal = (width: number, height: number): boolean => {
  const isLandscape = width > height;
  return (width >= 768 && width <= 1024) || (isLandscape && width <= 1024);
};

/**
 * Gets comprehensive screen size information
 * @param width - Current viewport width
 * @param height - Current viewport height
 * @returns Object with all screen size boolean flags
 */
export const getScreenSizeInfo = (width: number, height: number) => {
  return {
    isMobile: isMobileDevice(width),
    isSmallScreen: isSmallScreen(width),
    isMobileHorizontal: isMobileHorizontal(width, height),
    isLandscape: width > height,
    isPortrait: height > width,
    width,
    height,
  };
};

/**
 * Calculates responsive height for tables and containers based on viewport and device type
 * @param width - Current viewport width
 * @param height - Current viewport height (optional)
 * @param baseOffset - Base offset to subtract from viewport height (default: 350)
 * @returns Object with calculated height and minHeight
 */
export const getResponsiveTableHeight = (
  width: number,
  height?: number,
  baseOffset: number = 350,
): { height: string; minHeight: string } => {
  const isMobile = isMobileDevice(width);

  if (!isMobile) {
    // Desktop/tablet portrait mode
    return {
      height: `calc(100vh - ${baseOffset}px)`,
      minHeight: "400px",
    };
  }

  // Mobile/landscape mode - use actual height if available for better calculations
  if (height && height <= 600) {
    // Landscape mode on mobile devices
    const landscapeOffset = height <= 400 ? 120 : height <= 500 ? 150 : 200;
    return {
      height: `calc(100vh - ${landscapeOffset}px)`,
      minHeight: height <= 400 ? "200px" : height <= 500 ? "250px" : "300px",
    };
  }

  // Mobile portrait mode - check for tall screens and reduce height more
  if (height && height > width) {
    // Portrait mode: reduce height significantly to show the button
    // Account for button height (~48px) + margin (24px) + some padding (~50px) = ~120px
    const portraitOffset = height > 800 ? 400 : height > 600 ? 350 : 300;
    return {
      height: `calc(100vh - ${portraitOffset}px)`,
      minHeight: "200px",
    };
  }

  // Mobile portrait or when height is not available
  // Reduce height more aggressively to ensure button visibility
  return {
    height: `calc(100vh - 350px)`,
    minHeight: "200px",
  };
};

/**
 * Gets responsive container styles for ag-grid tables
 * @param width - Current viewport width
 * @param height - Current viewport height (optional)
 * @returns Style object with responsive dimensions
 */
export const getResponsiveTableStyles = (
  width: number,
  height?: number,
): React.CSSProperties => {
  const { height: calculatedHeight, minHeight } = getResponsiveTableHeight(
    width,
    height,
  );

  return {
    height: calculatedHeight,
    minHeight,
    padding: 10,
  };
};
